import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { UseFormRegister, FieldErrors, UseFormSetValue, SubmitHandler } from 'react-hook-form';
import type { SavedScenario, ReverseCalculationInput, AdvancedTaxOptions, AgeCategory } from '../../types/tax';
import { calculateReverseIncome } from '../../utils/calculations';
import { validateNetIncome } from '../../utils/validation';
import CalculationFormData, { type FormData } from './CalculationFormData';

interface EditScenarioModalProps {
  isOpen: boolean;
  scenario: SavedScenario | null;
  onClose: () => void;
  onSave: (id: string, updates: Partial<Pick<SavedScenario, 'name' | 'input' | 'result' | 'inputFrequency'>>) => void;
}

export default function EditScenarioModal({ isOpen, scenario, onClose, onSave }: EditScenarioModalProps) {
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm<FormData & { name: string }>({
    defaultValues: {
      name: scenario?.name || '',
      income: scenario ? formatInitialIncome(scenario) : '',
      ageCategory: scenario?.input.ageCategory || '',
      incomeFrequency: scenario?.inputFrequency || 'monthly',
      showAdvancedOptions: hasAdvancedOptions(scenario),
      // Medical Aid defaults
      hasMedicalAid: scenario?.input.advancedOptions?.medicalAid?.hasMedicalAid || false,
      medicalAidMainMember: scenario?.input.advancedOptions?.medicalAid?.mainMember || false,
      medicalAidDependents: scenario?.input.advancedOptions?.medicalAid?.dependents?.toString() || '0',
      // Pension defaults
      hasPension: scenario?.input.advancedOptions?.pension?.hasPension || false,
      pensionType: scenario?.input.advancedOptions?.pension?.contributionType || 'percentage',
      pensionAmount: formatInitialPension(scenario),
      // Travel Allowance defaults
      hasTravelAllowance: scenario?.input.advancedOptions?.travelAllowance?.hasTravelAllowance || false,
      vehicleValue: scenario?.input.advancedOptions?.travelAllowance?.vehicleValue?.toString() || '',
      privateUsePercentage: scenario?.input.advancedOptions?.travelAllowance?.privateUsePercentage?.toString() || '0',
      travelAllowanceAmount: scenario?.input.advancedOptions?.travelAllowance?.travelAllowanceAmount?.toString() || '',
    }
  });

  const watchedValues = watch();

  // Reset form values when scenario changes
  useEffect(() => {
    if (scenario) {
      reset({
        name: scenario.name,
        income: formatInitialIncome(scenario),
        ageCategory: scenario.input.ageCategory,
        incomeFrequency: scenario.inputFrequency,
        showAdvancedOptions: hasAdvancedOptions(scenario),
        // Medical Aid defaults
        hasMedicalAid: scenario.input.advancedOptions?.medicalAid?.hasMedicalAid || false,
        medicalAidMainMember: scenario.input.advancedOptions?.medicalAid?.mainMember || false,
        medicalAidDependents: scenario.input.advancedOptions?.medicalAid?.dependents?.toString() || '0',
        // Pension defaults
        hasPension: scenario.input.advancedOptions?.pension?.hasPension || false,
        pensionType: scenario.input.advancedOptions?.pension?.contributionType || 'percentage',
        pensionAmount: formatInitialPension(scenario),
        // Travel Allowance defaults
        hasTravelAllowance: scenario.input.advancedOptions?.travelAllowance?.hasTravelAllowance || false,
        vehicleValue: scenario.input.advancedOptions?.travelAllowance?.vehicleValue?.toString() || '',
        privateUsePercentage: scenario.input.advancedOptions?.travelAllowance?.privateUsePercentage?.toString() || '0',
        travelAllowanceAmount: scenario.input.advancedOptions?.travelAllowance?.travelAllowanceAmount?.toString() || '',
      });
    }
  }, [scenario, reset]);

  // Helper functions to format initial values
  function formatInitialIncome(scenario: SavedScenario): string {
    const amount = scenario.inputFrequency === 'monthly' 
      ? scenario.input.desiredNetIncome / 12
      : scenario.input.desiredNetIncome;
    return `R ${Math.round(amount).toLocaleString('en-ZA')}`;
  }

  function formatInitialPension(scenario: SavedScenario | null): string {
    if (!scenario?.input.advancedOptions?.pension?.hasPension) return '';
    const amount = scenario.input.advancedOptions.pension.contributionAmount;
    if (scenario.input.advancedOptions.pension.contributionType === 'percentage') {
      return amount.toString();
    } else {
      // Convert annual to monthly if scenario is monthly
      const displayAmount = scenario.inputFrequency === 'monthly' ? amount / 12 : amount;
      return Math.round(displayAmount).toString();
    }
  }

  function hasAdvancedOptions(scenario: SavedScenario | null): boolean {
    if (!scenario?.input.advancedOptions) return false;
    const opts = scenario.input.advancedOptions;
    return !!(opts.medicalAid?.hasMedicalAid || opts.pension?.hasPension || opts.travelAllowance?.hasTravelAllowance);
  }

  if (!isOpen || !scenario) return null;

  const onSubmit = async (data: FormData & { name: string }) => {
    try {
      setIsCalculating(true);
      setError(null);

      const netIncomeAmount = parseFloat(data.income.replace(/[^\d.]/g, ''));
      
      // Convert to annual if input is monthly
      const annualNetIncome = data.incomeFrequency === 'monthly' 
        ? netIncomeAmount * 12 
        : netIncomeAmount;
      
      const validationResult = validateNetIncome(annualNetIncome);
      if (!validationResult.isValid) {
        setError(validationResult.message || 'Validation error');
        return;
      }

      // Build advanced options if enabled
      let advancedOptions: AdvancedTaxOptions | undefined;
      
      if (data.showAdvancedOptions) {
        advancedOptions = {};
        
        // Medical Aid options
        if (data.hasMedicalAid) {
          advancedOptions.medicalAid = {
            hasMedicalAid: true,
            mainMember: data.medicalAidMainMember,
            dependents: parseInt(data.medicalAidDependents) || 0,
          };
        }
        
        // Pension options        
        if (data.hasPension && data.pensionAmount) {
          const pensionAmount = parseFloat(data.pensionAmount.replace(/[^\d.]/g, '')) || 0;                    
          if (pensionAmount > 0) {            
            // Convert monthly pension to annual if needed
            const annualPensionAmount = data.pensionType === 'fixed' && data.incomeFrequency === 'monthly'
              ? pensionAmount * 12
              : pensionAmount;            
            
            advancedOptions.pension = {
              hasPension: true,
              contributionType: data.pensionType,
              contributionAmount: annualPensionAmount,
            };
          }
        }
        
        // Travel Allowance options
        if (data.hasTravelAllowance && (data.vehicleValue || data.travelAllowanceAmount)) {
          advancedOptions.travelAllowance = {
            hasTravelAllowance: true,
            vehicleValue: parseFloat(data.vehicleValue) || 0,
            privateUsePercentage: parseFloat(data.privateUsePercentage) || 0,
            travelAllowanceAmount: parseFloat(data.travelAllowanceAmount) || 0,
          };
        }
      }

      const input: ReverseCalculationInput = {
        desiredNetIncome: annualNetIncome,
        ageCategory: data.ageCategory as AgeCategory,
        advancedOptions,
      };

      const result = calculateReverseIncome(input);
      
      // Save the updated scenario
      onSave(scenario.id, {
        name: data.name,
        input,
        result,
        inputFrequency: data.incomeFrequency,
      });

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      console.error('Calculation error:', err);
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-charcoal dark:text-slate-100 mb-4">Edit Scenario</h3>
        
        <form onSubmit={handleSubmit(onSubmit as SubmitHandler<FormData & { name: string }>)} className="space-y-6">
          {/* Scenario Name */}
          <div>
            <label htmlFor="scenarioName" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              Scenario Name
            </label>
            <input
              type="text"
              id="scenarioName"
              {...register('name', { required: 'Please enter a scenario name' })}
              className="input-field w-full"
              disabled={isCalculating}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-error">{errors.name.message}</p>
            )}
          </div>

          {/* Calculation Form */}
          <CalculationFormData
            register={register as unknown as UseFormRegister<FormData>}
            errors={errors as unknown as FieldErrors<FormData>}
            isCalculating={isCalculating}
            watchedValues={watchedValues as FormData}
            setValue={setValue as unknown as UseFormSetValue<FormData>}
            showPresets={false}
            showFrequencyToggle={true}
          />

          {/* Error Display */}
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-error" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-error">Error</h3>
                  <p className="text-sm text-error mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
              disabled={isCalculating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex-1"
              disabled={isCalculating || !watchedValues.name?.trim() || !watchedValues.income || !watchedValues.ageCategory}
            >
              {isCalculating ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Updating...
                </>
              ) : (
                'Update Scenario'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}